import React, { useCallback, useContext, useEffect, useState } from 'react'
import Alert from '@mui/material/Alert'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import Chip from '@mui/material/Chip'
import Skeleton from '@mui/material/Skeleton'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import AddLinkIcon from '@mui/icons-material/AddLink'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import API from '../../../../services/API'
import DetailsBox from '../../../../components/Cards/DetailsBox'
import SubscriptionGate from '../../../../components/Subscriptions/SubscriptionGate'
import { GlobalContext } from '../../../../App'
import { EPCRContext } from '../EPCR'
import ShareCodeCard from './ShareCodeCard'
import CreateShareCodeDialog from './CreateShareCodeDialog'
import ShareCodeDetailsDialog from './ShareCodeDetailsDialog'
import {
  getAccessRecordUrl,
  getShareCodeStatus,
  sortNewestFirst,
} from './shareCodeUtils'

// The record's share codes: used from the ePCR Sharing module, the report
// view and the My Recents popup. `passedCodes` (or the edit form's values)
// seed the list; the authoritative list is then fetched, so codes created
// elsewhere (e.g. on mobile) show up too.
const ShareCode = ({
  ePCRID,
  loadRecord,
  passedCodes,
  currentTreatmentLocation,
}) => {
  const { formValues } = useContext(EPCRContext)
  const { setToast } = useContext(GlobalContext)

  const holder =
    currentTreatmentLocation ?? formValues?.review?.currentTreatmentLocation
  const hasTreatmentLocationOwner = !!holder
  const treatmentLocationName = holder?.name || 'a treatment centre'

  const [codes, setCodes] = useState(
    () => passedCodes ?? formValues?.review?.shareCodes ?? [],
  )
  const [loading, setLoading] = useState(!!ePCRID)
  const [loadError, setLoadError] = useState(false)
  const [showInactive, setShowInactive] = useState(false)
  const [createOpen, setCreateOpen] = useState(false)
  const [selectedID, setSelectedID] = useState(null)

  useEffect(() => {
    if (passedCodes) setCodes(passedCodes)
    else if (formValues?.review?.shareCodes) {
      setCodes(formValues.review.shareCodes)
    }
  }, [passedCodes, formValues?.review?.shareCodes])

  const loadCodes = useCallback(() => {
    if (!ePCRID) return
    setLoading(true)
    setLoadError(false)
    API.get(`/epcr/share-codes/${ePCRID}`)
      .then((res) => {
        if (res.status === 200) setCodes(res.data)
        // 403: not allowed to list — keep whatever came with the record
        else if (res.status !== 403) setLoadError(true)
      })
      .catch(() => setLoadError(true))
      .finally(() => setLoading(false))
  }, [ePCRID])

  useEffect(() => {
    loadCodes()
  }, [loadCodes])

  const copy = (text, label) => {
    if (!navigator.clipboard?.writeText) {
      setToast?.({ message: 'Copying is not available in this browser', severity: 'error' })
      return
    }
    navigator.clipboard
      .writeText(text)
      .then(() => setToast?.({ message: `${label} copied` }))
      .catch(() =>
        setToast?.({ message: `Unable to copy the ${label.toLowerCase()}`, severity: 'error' }),
      )
  }

  const upsertCode = (code) =>
    setCodes((prev) =>
      prev.some((entry) => entry._id === code._id)
        ? prev.map((entry) =>
            entry._id === code._id ? { ...entry, ...code } : entry,
          )
        : [code, ...prev],
    )

  const handleRevoked = (code) => {
    upsertCode(code)
    setToast?.({ message: 'Share code revoked' })
    loadRecord?.()
  }

  const sorted = sortNewestFirst(codes)
  const active = sorted.filter((code) => getShareCodeStatus(code) === 'active')
  const inactive = sorted.filter((code) => getShareCodeStatus(code) !== 'active')
  const expiredCount = inactive.filter(
    (code) => getShareCodeStatus(code) === 'expired',
  ).length
  const revokedCount = inactive.length - expiredCount
  const visible = showInactive ? sorted : active
  const selectedCode = selectedID
    ? codes.find((code) => code._id === selectedID) ?? null
    : null
  const canCreate = !!ePCRID && !hasTreatmentLocationOwner

  return (
    <DetailsBox
      title="Share codes"
      caption="Give a hospital or receiving service temporary access to download this record. They need the code plus the patient's surname and date of birth."
      buttonRight={
        <Button
          size="small"
          variant="contained"
          color="anMainBlue"
          startIcon={<AddLinkIcon />}
          onClick={() => setCreateOpen(true)}
          disabled={!canCreate}
        >
          New share code
        </Button>
      }
    >
      <SubscriptionGate
        inline
        requiredTier={2}
        featureName="ePCR Share Codes"
        areNotIs
        benefits={[
          'Share ePCRs securely',
          'Provide a single access code for authority',
          'Prevent open-ended access to patient data',
        ]}
      >
        <Stack gap={1.5}>
          {hasTreatmentLocationOwner && (
            <Alert severity="info">
              This record is held by {treatmentLocationName}. Share codes can
              only be created by the current owner.
            </Alert>
          )}
          {!ePCRID && (
            <Alert severity="info">
              Save the record before creating a share code.
            </Alert>
          )}

          <Stack
            direction="row"
            gap={1}
            alignItems="center"
            justifyContent="space-between"
            flexWrap="wrap"
          >
            <Stack direction="row" gap={1} flexWrap="wrap">
              <Chip
                size="small"
                color="success"
                variant={active.length ? 'filled' : 'outlined'}
                label={`${active.length} active`}
                sx={{ borderRadius: 1, fontWeight: 600 }}
              />
              <Chip
                size="small"
                variant="outlined"
                label={`${expiredCount} expired`}
                sx={{ borderRadius: 1, fontWeight: 600 }}
              />
              <Chip
                size="small"
                variant="outlined"
                color={revokedCount ? 'error' : 'default'}
                label={`${revokedCount} revoked`}
                sx={{ borderRadius: 1, fontWeight: 600 }}
              />
            </Stack>
            <Button
              size="small"
              variant="text"
              startIcon={<ContentCopyIcon />}
              onClick={() => copy(getAccessRecordUrl(), 'Access link')}
            >
              Copy access link
            </Button>
          </Stack>

          {loadError && (
            <Alert severity="warning">
              Unable to refresh share codes. Showing what was loaded with the
              record.
            </Alert>
          )}

          {loading && codes.length === 0 ? (
            <Skeleton variant="rounded" height={72} />
          ) : visible.length === 0 ? (
            <Card
              variant="outlined"
              sx={{ p: 2, textAlign: 'center', borderStyle: 'dashed' }}
            >
              <Typography variant="body2" fontWeight={600}>
                {inactive.length > 0 ? 'No active share codes' : 'No share codes yet'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Codes expire automatically after at most 24 hours and can be
                revoked at any time.
              </Typography>
            </Card>
          ) : (
            <Stack gap={1}>
              {visible.map((code) => (
                <ShareCodeCard
                  key={code._id ?? code.code}
                  shareCode={code}
                  onOpen={() => setSelectedID(code._id)}
                  onCopy={() => copy(code.code, 'Share code')}
                />
              ))}
            </Stack>
          )}

          {inactive.length > 0 && (
            <Button
              size="small"
              variant="text"
              color="inherit"
              startIcon={showInactive ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              onClick={() => setShowInactive((prev) => !prev)}
              sx={{ alignSelf: 'flex-start' }}
            >
              {showInactive
                ? 'Hide expired and revoked codes'
                : `Show ${inactive.length} expired or revoked code${
                    inactive.length === 1 ? '' : 's'
                  }`}
            </Button>
          )}
        </Stack>
      </SubscriptionGate>

      <CreateShareCodeDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        ePCRID={ePCRID}
        onCreated={upsertCode}
        onCopy={copy}
      />
      <ShareCodeDetailsDialog
        shareCode={selectedCode}
        open={!!selectedCode}
        onClose={() => setSelectedID(null)}
        onRevoked={handleRevoked}
        onCopy={copy}
      />
    </DetailsBox>
  )
}

export default ShareCode
